import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";

const Nav = ({ toggleDarkMode, isDarkMode, currentPage = "skills" }) => {
  const [activeSection, setActiveSection] = useState("home");
  const [sectionProgress, setSectionProgress] = useState({});
  const [sections, setSections] = useState(["home", "about", "skills", "projects", "interests"]);

  const lastScrollY = useRef(0);
  const [scrollDirection, setScrollDirection] = useState("down");

  useEffect(() => {
    // 根据当前页面更新导航项
    const updatedSections = ["home", "about", currentPage, "projects", "interests"];
    setSections(updatedSections);
  }, [currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      // 加入滚动方向判断 + 容差控制
      if (Math.abs(delta) > 5) {
        setScrollDirection(delta > 0 ? "down" : "up");
        lastScrollY.current = currentY;
      }

      const newProgress = {};
      sections.forEach((id) => {
        const section = document.getElementById(id);
        if (section) {
          const rect = section.getBoundingClientRect();
          const height = section.offsetHeight;
          const top = rect.top;
          const windowHeight = window.innerHeight;

          let progress = 0;
          if (top < windowHeight && top + height > 0) {
            const visibleHeight = Math.min(windowHeight, top + height) - Math.max(0, top);
            progress = visibleHeight / height;
            progress = Math.max(0, Math.min(1, progress));
          }

          newProgress[id] = progress;
        }
      });

      setSectionProgress(newProgress);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // 初始调用一次以设置初始状态
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]); // 添加 sections 作为依赖项

  const linkClass = (id) => `nav-link cursor-pointer transition duration-300`;

  // 计算从灰到白的过渡
  const interpolateColor = (progress) => {
    const startRGB = [136, 136, 136]; // 灰色 #888
    const endRGB = [255, 255, 255]; // 白色 #fff

    const resultRGB = startRGB.map((start, i) =>
      Math.round(start + (endRGB[i] - start) * progress)
    );

    return `rgb(${resultRGB.join(",")})`;
  };

  const linkStyle = (id) => {
    const percent = sectionProgress[id] || 0;
    const fill = Math.floor(percent * 100);
    const whiteOrBlack = isDarkMode ? "white" : "black";
    const direction = scrollDirection === "down" ? "to top" : "to bottom";

    return {
      backgroundImage: `linear-gradient(${direction}, ${whiteOrBlack} ${fill}%, #adadad ${fill}%)`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      transition: "background 0.2s ease",
    };
  };

  return (
    <nav className="fixed top-1/2 -translate-y-1/2 right-3 flex">
      <div className="container mx-auto">
        <div className="h-full flex flex-col text-2xl gap-y-2 text-right font-bold">
          {sections.map((id) => (
            id === currentPage ? (
              <AnimatePresence mode="wait" key={id}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link
                    to={id}
                    smooth={true}
                    spy={true}
                    duration={500}
                    offset={-50}
                    className={linkClass(id)}
                    style={linkStyle(id)}
                    onClick={() => setActiveSection(id)}
                  >
                    {id.charAt(0).toUpperCase() + id.slice(1)}
                  </Link>
                </motion.div>
              </AnimatePresence>
            ) : (
              <Link
                key={id}
                to={id}
                smooth={true}
                spy={true}
                duration={500}
                offset={-50}
                className={linkClass(id)}
                style={linkStyle(id)}
                onClick={() => setActiveSection(id)}
              >
                {id.charAt(0).toUpperCase() + id.slice(1)}
              </Link>
            )
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Nav;
